import useUnsavedChanges from '../hooks/useUnsavedChanges';
import React, { useEffect, useRef } from 'react';
import './tokens.css';
export function Button({
  variant = '',
  busy = false,
  children,
  className = '',
  ...props
}) {
  return <button type="button" {...props} disabled={props.disabled || busy} aria-busy={busy} className={'btn ui-button ' + variant + ' ' + className}>{busy ? 'Speichert …' : children}</button>;
}
export function PageHeader({
  title,
  description,
  children
}) {
  return <header className="ui-header"><div><h1>{title}</h1><p>{description}</p></div><div className="ui-actions">{children}</div></header>;
}
export function StatTile({
  label,
  value,
  description
}) {
  return <section className="ui-stat"><small>{label}</small><strong>{value}</strong><small>{description}</small></section>;
}
export function EmptyState({
  children,
  action
}) {
  return <div className="ui-empty"><p>{children}</p>{action}</div>;
}
export function Skeleton({
  rows = 4
}) {
  return <div role="status" aria-label="Wird geladen">{Array.from({
      length: rows
    }, (_, i) => <div className="ui-skeleton" key={i} />)}</div>;
}
export function Field({
  label,
  children
}) {
  const id = React.useId();
  const child = React.Children.count(children) === 1 ? React.Children.toArray(children)[0] : null;
  const native = React.isValidElement(child) && ['input', 'select', 'textarea'].includes(child.type);
  return native ? <div className="ui-field"><label htmlFor={child.props.id || id}>{label}</label>{React.cloneElement(child, {
      id: child.props.id || id
    })}</div> : <div className="ui-field" role="group" aria-label={label}><span>{label}</span>{children}</div>;
}
export function Badge({
  children
}) {
  return <span className="ui-badge">{children}</span>;
}
export function Icon({
  name = 'box',
  size = 18,
  ...props
}) {
  const paths = {
    calendar: 'M4 5h16v16H4zM8 2v6M16 2v6M4 10h16',
    briefcase: 'M3 7h18v14H3zM8 7V3h8v4M3 12h18M10 12v3h4v-3',
    truck: 'M2 5h12v12H2zM14 9h4l4 5v3h-8M5 17a2 2 0 1 0 0 4 2 2 0 0 0 0-4M18 17a2 2 0 1 0 0 4 2 2 0 0 0 0-4',
    users: 'M16 21v-3a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v3M9 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8M18 4a4 4 0 0 1 0 8M22 21v-3a4 4 0 0 0-3-4',
    map: 'M3 5l6-2 6 2 6-2v16l-6 2-6-2-6 2V5M9 3v16M15 5v16',
    settings: 'M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8M9 3h6l1 3 3 1 2 5-2 5-3 1-1 3H9l-1-3-3-1-2-5 2-5 3-1z',
    shield: 'M12 2l9 4v6c0 5-9 10-9 10S3 17 3 12V6zM8 12l3 3 5-6',
    monitor: 'M2 3h20v14H2zM12 17v4M7 21h10',
    gate: 'M4 22V3h16v19M7 7h10M7 11h10M7 15h10M7 19h10',
    panel: 'M3 3h18v18H3zM9 3v18',
    help: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20M9 8a3 3 0 0 1 6 0c0 2-3 2-3 5M12 16v1',
    logout: 'M9 3H3v18h6M8 12h13M16 7l5 5-5 5',
    menu: 'M3 6h18M3 12h18M3 18h18',
    pin: 'M8 3h8l-1 7 4 4H5l4-4-1-7M12 14v8',
    sun: 'M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8M12 1v3M12 20v3M1 12h3M20 12h3M4 4l2 2M18 18l2 2M4 20l2-2M18 6l2-2',
    moon: 'M20 15A9 9 0 0 1 9 4a9 9 0 1 0 11 11',
    box: 'M3 7l9-4 9 4v10l-9 4-9-4V7zm0 0l9 4 9-4M12 11v10',
    plus: 'M12 5v14M5 12h14',
    close: 'M6 6l12 12M18 6L6 18',
    search: 'M21 21l-5-5M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0',
    clock: 'M12 8v5l3 2M22 12a10 10 0 1 1-20 0 10 10 0 0 1 20 0'
  };
  return <svg {...props} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true"><path d={paths[name] || paths.box} /></svg>;
}
export function IconButton({
  label,
  icon,
  ...props
}) {
  return <Button {...props} aria-label={label} title={label}><Icon name={icon} /></Button>;
}
export function Surface({
  children,
  ...props
}) {
  return <section {...props} className={'ui-surface ' + (props.className || '')}>{children}</section>;
}
export const Panel = Surface;
export function Tabs({
  items,
  value,
  onChange,
  label
}) {
  return <div className="ui-actions" role="group" aria-label={label}>{items.map(item => <Button key={item.value} aria-pressed={value === item.value} variant={value === item.value ? 'primary' : ''} onClick={() => onChange(item.value)}>{item.label}</Button>)}</div>;
}
export function SearchInput(props) {
  return <input {...props} type="search" className="form-control ui-search" />;
}
export function Input(props) {
  return <input {...props} className="ui-search" />;
}
export function Select({
  children,
  ...props
}) {
  return <select {...props} className="form-select ui-search">{children}</select>;
}
export function MultiSelect({
  items,
  value,
  onChange,
  label
}) {
  return <Field label={label}><Select multiple value={value} onChange={e => onChange(Array.from(e.target.selectedOptions, o => o.value))}>{items.map(i => <option value={i.value} key={i.value}>{i.label}</option>)}</Select></Field>;
}
export function Checkbox({
  label,
  ...props
}) {
  return <label><input {...props} type="checkbox" /> {label}</label>;
}
export function Switch(props) {
  return <Checkbox {...props} role="switch" />;
}
export function DateInput(props) {
  return <Input {...props} type="date" />;
}
export function TimeInput(props) {
  return <Input {...props} type="time" />;
}
export function useDialogFocus(ref, onClose) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const previous = document.activeElement,
      root = ref.current;
    if (!root) return;
    const focusable = () => Array.from(root.querySelectorAll('button:not(:disabled),input:not(:disabled),select:not(:disabled),textarea:not(:disabled),a[href],[tabindex="0"]')).filter(el => !el.hidden);
    (root.querySelector('[autofocus]') || focusable()[0] || root).focus();
    const key = e => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeRef.current?.();
      }
      if (e.key === 'Tab') {
        const nodes = focusable(),
          first = nodes[0],
          last = nodes[nodes.length - 1];
        if (!first) {
          e.preventDefault();
          root.focus();
        } else if (e.shiftKey && (document.activeElement === first || document.activeElement === root)) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    root.addEventListener('keydown', key);
    return () => {
      root.removeEventListener('keydown', key);
      if (previous?.isConnected) previous.focus();
    };
  }, [ref]);
}
export function Modal({
  title,
  children,
  onClose
}) {
  const ref = useRef(null);
  useDialogFocus(ref, onClose);
  return <div className="ui-modal-backdrop"><section ref={ref} tabIndex={-1} role="dialog" aria-modal="true" aria-label={title} className="ui-modal"><PageHeader title={title}><IconButton icon="close" label="Schließen" onClick={onClose} /></PageHeader>{children}</section></div>;
}
export function CreatePanel({
  title,
  children
}) {
  const [open, setOpen] = React.useState(false);
  return <><div className="ui-actions"><Button variant="primary" onClick={() => setOpen(true)}><Icon name="plus" />{title}</Button></div>{open && <CreationDialog title={title} onClose={() => setOpen(false)}>{children}</CreationDialog>}</>;
}
function CreationDialog({
  title,
  onClose,
  children
}) {
  const [dirty, setDirty] = React.useState(false),
    [busy, setBusy] = React.useState(false),
    [error, setError] = React.useState('');
  const close = useUnsavedChanges(dirty, onClose);
  const form = React.cloneElement(children, {
    onChangeCapture: () => setDirty(true),
    onSubmit: async e => {
      e.preventDefault();
      if (busy) return;
      setBusy(true);
      setError('');
      try {
        const result = await children.props.onSubmit(e);
        if (result !== false) onClose();
      } catch (err) {
        setError(err.message);
      } finally {
        setBusy(false);
      }
    },
    'aria-busy': busy,
    children: <><fieldset disabled={busy} className="ws-create-fields">{children.props.children}</fieldset>{error && <p role="alert">{error}</p>}</>
  });
  return <Modal title={title} onClose={close}>{form}</Modal>;
}
export function Tooltip({
  label,
  children
}) {
  return <span title={label}>{children}</span>;
}
export function SectionHeading({
  children
}) {
  return <h2 className="ui-section-heading">{children}</h2>;
}
export function LoadingState({
  children = 'Wird geladen …'
}) {
  return <div role="status" aria-live="polite">{children}<Skeleton rows={1} /></div>;
}
export function ResourceEditor({
  title,
  value,
  onClose,
  onSave,
  children
}) {
  const [busy, setBusy] = React.useState(false),
    [error, setError] = React.useState('');
  const close = useUnsavedChanges(value, onClose);
  return <Modal title={title} onClose={close}><form onSubmit={async e => {
      e.preventDefault();
      if (busy) return;
      setBusy(true);
      setError('');
      try {
        await onSave();
      } catch (err) {
        setError(err.message);
      } finally {
        setBusy(false);
      }
    }}><div className="ws-editor-fields">{children}</div>{error && <p role="alert">{error}</p>}<div className="ui-actions"><Button onClick={close}>Abbrechen</Button><Button type="submit" busy={busy} variant="primary">Speichern</Button></div></form></Modal>;
}
